// src/shared_new/components/PerlTable.jsx
// Simple, reusable table component with Perl theme styling

import React from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
} from "@mui/material";
import { theme } from "../../design/theme";

const { perlColors } = theme.custom;

/**
 * PerlTable - Simple reusable table component
 *
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions: [{ id, label, width, align }]
 * @param {Array} props.data - Array of row data objects
 * @param {Function} props.renderRow - Function to render each row: (row, index) => React.Node
 * @param {Object} props.headerActions - Optional header actions component
 * @param {String} props.size - Table size: "small" | "medium"
 * @param {Object} props.sx - Additional styling
 */
export default function PerlTable({
  columns = [],
  data = [],
  renderRow,
  headerActions = null,
  size = "small",
  sx = {},
}) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        bgcolor: "transparent",
        backgroundImage: "none",
        boxShadow: "none",
        ...sx,
      }}
    >
      {headerActions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: `1px solid ${perlColors.border}`,
          }}
        >
          {headerActions}
        </Box>
      )}

      <Table
        size={size}
        sx={{
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${perlColors.textSecondary}`,
            color: perlColors.textPrimary,
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align || "left"}
                sx={{
                  color: perlColors.textSecondary,
                  fontWeight: 600,
                  width: col.width || "auto",
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                align="center"
                sx={{ color: perlColors.textSecondary, py: 4 }}
              >
                No data available
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, index) => renderRow(row, index))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
